import { Controller } from "@hotwired/stimulus";

// Any letter or digit, in any script — the JS counterpart of Ruby's
// [[:alnum:]]. Built from a string rather than written as a regex literal
// because this project's eslint parser reads \p{...} in a literal as a
// useless escape.
const ALPHANUMERIC = new RegExp("[\\p{L}\\p{N}]", "u");

// Normalizes the Automatic Hashtag Tracking field in the browser, so the value
// the user leaves behind is the value the server will actually store.
//
// The field expects hashtags separated by spaces and a grey hint said so, but
// nothing enforced it and typing the natural thing — commas — silently
// corrupted the search: a stored "#One , #Two" split into ["#One", ",",
// "#Two"], and the bare "," became a search term meaning "caption contains a
// comma", true of nearly every caption ever written. The server normalizes on
// write now (SavedSearchQuery + SavedSearchValidations), so this is not the
// fix — it is the feedback. Without it the field keeps accepting commas, the
// save succeeds, and the user never learns their input was rewritten.
//
// Mirrors SavedSearchQuery#normalize. Keep the two in step; the server is the
// authority and the only one that has to be right.
export default class extends Controller {
  static targets = ["input", "notice"];

  connect() {
    this.form = this.element.closest("form");
    if (this.form) {
      this.form.addEventListener("submit", this.normalize);
    }
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("submit", this.normalize);
    }
    clearTimeout(this.noticeTimeout);
  }

  // A bound property rather than a method so add/removeEventListener see the
  // same function, and so it can also be wired as a Stimulus action on blur.
  normalize = () => {
    if (!this.hasInputTarget) return;

    const original = this.inputTarget.value;
    const normalized = this.normalizeQuery(original);

    if (normalized === original) return;

    this.inputTarget.value = normalized;
    this.announce();
  };

  // Terms are separated by whitespace or commas. A term with no letter or
  // digit is separator debris, not something to search for.
  normalizeQuery(value) {
    const seen = new Set();

    return value
      .split(/[,\s]+/)
      .filter((term) => ALPHANUMERIC.test(term))
      .filter((term) => {
        const key = term.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .join(" ");
  }

  announce() {
    if (!this.hasNoticeTarget) return;

    this.noticeTarget.classList.remove("hidden");
    clearTimeout(this.noticeTimeout);
    this.noticeTimeout = setTimeout(() => {
      this.noticeTarget.classList.add("hidden");
    }, 6000);
  }
}
